import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Receipt, DollarSign, AlertTriangle, CheckCircle2, Info,
  Droplets, TrendingUp, Building2, RefreshCw,
  MapPin, Calculator, BarChart3, Gauge, ExternalLink,
  ChevronDown, ChevronRight, FileText, Shield, Activity,
  Save, Download, History, Loader2, X, CheckCircle, Check,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, CartesianGrid,
} from 'recharts';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import {
  apiListCustomerSites,
  apiListCustomerSamples,
  apiListInfluentSamples,
  apiSaveSAWaterEstimate,
  apiListSAWaterEstimates,
  hasAuthToken,
} from '@/lib/phpApi';
import {
  generateSAWaterChargesReport,
  buildRegulatoryReportNumber,
} from '@/lib/pdfGenerator';
import type { SAWaterChargeRow } from '@/lib/pdfGenerator';

// ─── SA Water Charge Rate Table ───────────────────────────────────────────────
const SA_WATER_RATES = [
  {
    key: 'bod5', label: 'BOD₅', fullName: 'Biochemical Oxygen Demand',
    unit: 'mg/L', permitLimit: 3000,
    lowRate: 0.251, highRate: 0.378,
    threshold: 1000,
    triggerKg: 10000,
    tknCondition: false,
    color: '#f97316',
  },
  {
    key: 'tss', label: 'SS', fullName: 'Suspended Solids',
    unit: 'mg/L', permitLimit: 500,
    lowRate: 0.218, highRate: 0.315,
    threshold: 500,
    triggerKg: 10000,
    tknCondition: false,
    color: '#eab308',
  },
  {
    key: 'tds', label: 'TDS', fullName: 'Total Dissolved Solids',
    unit: 'mg/L', permitLimit: 1500,
    lowRate: 0.126, highRate: 0.126,
    threshold: null,
    triggerKg: 20000,
    tknCondition: false,
    color: '#3b82f6',
  },
  {
    key: 'tn', label: 'TKN', fullName: 'Total Kjeldahl Nitrogen',
    unit: 'mg/L', permitLimit: 130,
    lowRate: 0.392, highRate: 0.392,
    threshold: null,
    triggerKg: null,
    tknCondition: true,
    color: '#8b5cf6',
  },
  {
    key: 'totalPhosphorus', label: 'TP', fullName: 'Total Phosphorus',
    unit: 'mg/L', permitLimit: 15,
    lowRate: 1.908, highRate: 1.908,
    threshold: null,
    triggerKg: null,
    tknCondition: false,
    color: '#ec4899',
  },
] as const;

const PERMIT_LIMITS = [
  { label: 'pH',               range: '6 – 10',   unit: '',      key: 'ph',              limitHigh: 10,   limitLow: 6,   isRange: true,  color: '#22c55e' },
  { label: 'Temperature',      range: '≤ 38',     unit: '°C',   key: 'temperature',     limitHigh: 38,   limitLow: null, isRange: false, color: '#f59e0b' },
  { label: 'Oil & Grease',     range: '≤ 100',    unit: 'mg/L', key: 'oilGrease',       limitHigh: 100,  limitLow: null, isRange: false, color: '#f97316' },
  { label: 'Suspended Solids', range: '≤ 500',    unit: 'mg/L', key: 'tss',             limitHigh: 500,  limitLow: null, isRange: false, color: '#eab308' },
  { label: 'BOD₅',             range: '< 3,000',  unit: 'mg/L', key: 'bod5',            limitHigh: 3000, limitLow: null, isRange: false, color: '#f97316' },
  { label: 'TKN',              range: '≤ 130',    unit: 'mg/L', key: 'tn',              limitHigh: 130,  limitLow: null, isRange: false, color: '#8b5cf6' },
  { label: 'Total Phosphorus', range: '≤ 15',     unit: 'mg/L', key: 'totalPhosphorus', limitHigh: 15,   limitLow: null, isRange: false, color: '#ec4899' },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtNum(n: number, dp = 1) {
  return n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
function fmtAUD(n: number) {
  return `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function parseParams(raw: unknown): Record<string, number> {
  if (!raw) return {};
  const obj =
    typeof raw === 'string'
      ? (() => { try { return JSON.parse(raw); } catch { return {}; } })()
      : raw;
  return obj as Record<string, number>;
}
function extractValue(params: Record<string, number>, key: string): number | null {
  const aliases: Record<string, string[]> = {
    bod5: ['bod5', 'bod', 'BOD5', 'BOD'],
    tss: ['tss', 'TSS', 'ss', 'SS'],
    tds: ['tds', 'TDS'],
    tn: ['tn', 'TN', 'tkn', 'TKN', 'totalNitrogen'],
    totalPhosphorus: ['totalPhosphorus', 'tp', 'TP', 'phosphorus'],
    ph: ['ph', 'pH'],
    temperature: ['temperature', 'temp', 'Temperature'],
    oilGrease: ['oilGrease', 'oilgrease', 'oil_grease', 'OilGrease'],
  };
  const keys = aliases[key] ?? [key];
  for (const k of keys) {
    const v = params[k];
    if (typeof v === 'number' && v >= 0) return v;
  }
  return null;
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface ApiSite {
  id: number;
  name: string;
  address?: string;
  postcode?: string;
  standard?: string;
  permitRef?: string;
  permit_ref?: string;
}
interface ApiSample {
  id: number;
  // influent_samples table uses camelCase
  sampleName?: string;
  reportNumber?: string | null;
  siteId?: number | string | null;
  createdAt?: string;
  sampleType?: string;
  // customer_samples table uses snake_case
  sample_name?: string;
  report_number?: string | null;
  site_id?: number | string | null;
  created_at?: string;
  sample_type?: string;
  sample_date?: string;
  params?: unknown;
}
type ChargeRow = {
  key: string; label: string; fullName: string; color: string;
  avgMgL: number | null; loadKg: number | null;
  rate: number; charge: number | null;
  triggered: boolean; exceeds: boolean; highRateApplied: boolean;
};

// ─── Site Card ────────────────────────────────────────────────────────────────
function SiteCard({ site, selected, onClick }: {
  site: ApiSite; selected: boolean; onClick: () => void;
}) {
  const permitRef = site.permitRef || site.permit_ref;
  return (
    <motion.button
      whileHover={{ y: selected ? 0 : -1 }}
      whileTap={{ scale: 0.985 }}
      onClick={onClick}
      className={`text-left w-full rounded-xl border p-3.5 transition-all duration-200 ${
        selected
          ? 'border-teal-400/70 bg-teal-500/18 shadow-xl shadow-teal-500/20 ring-1 ring-teal-500/30'
          : 'border-slate-800/60 bg-slate-800/50 hover:border-teal-600/40 hover:bg-slate-800/80'
      }`}
      style={selected ? { background: 'rgba(20,184,166,0.14)' } : {}}
    >
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          selected ? 'bg-teal-500/30 ring-1 ring-teal-400/40' : 'bg-slate-700/60'
        }`}>
          <Building2 className={`w-3.5 h-3.5 ${selected ? 'text-teal-300' : 'text-slate-400'}`} />
        </div>
        {selected ? (
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-teal-500 text-white font-black uppercase tracking-wide">
              ACTIVE
            </span>
            <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center">
              <CheckCircle2 className="w-3 h-3 text-white" />
            </div>
          </div>
        ) : null}
      </div>

      <p className={`font-bold text-[12px] leading-tight ${selected ? 'text-teal-100' : 'text-slate-200'}`}>
        {site.name}
      </p>

      {site.address && (
        <div className="flex items-start gap-1 mt-1.5">
          <MapPin className={`w-2.5 h-2.5 mt-0.5 shrink-0 ${selected ? 'text-teal-500/80' : 'text-slate-500'}`} />
          <p className={`text-[9px] leading-snug ${selected ? 'text-teal-400/70' : 'text-slate-500'}`}>
            {site.address}{site.postcode ? ` ${site.postcode}` : ''}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wide border ${
          selected ? 'bg-teal-400/20 text-teal-200 border-teal-400/40' : 'bg-teal-500/15 text-teal-400 border-teal-500/20'
        }`}>
          <Droplets className="w-2 h-2" />SA Water
        </span>
        {permitRef && (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-mono border ${
            selected ? 'bg-teal-500/15 text-teal-300 border-teal-500/30' : 'bg-slate-700/50 text-slate-500 border-slate-800/60'
          }`}>
            <FileText className="w-2 h-2" />{permitRef}
          </span>
        )}
      </div>
    </motion.button>
  );
}

// ─── Chart tooltip ────────────────────────────────────────────────────────────
function CustomChartTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-slate-800 border border-slate-600/60 rounded-xl shadow-xl px-3 py-2.5">
      <p className="text-[10px] font-bold text-slate-300">{d.payload.label}</p>
      <p className="text-[13px] font-black text-slate-100 mt-0.5 tabular-nums">{fmtAUD(d.value)}</p>
      <p className="text-[9px] text-slate-500 mt-0.5">{d.payload.fullName}</p>
    </div>
  );
}

// ─── Permit Limits panel ──────────────────────────────────────────────────────
function PermitLimitsPanel({ avgs }: { avgs: Record<string, number | null> }) {
  const [open, setOpen] = useState(true);
  const hasData = PERMIT_LIMITS.some(l => avgs[l.key] !== null && avgs[l.key] !== undefined);

  return (
    <div className="rounded-xl border border-slate-800/60 bg-slate-800/40 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-800/40"
      >
        <div className="flex items-center gap-2">
          <Shield className="w-3.5 h-3.5 text-teal-500" />
          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">
            Trade Waste Permit Limits
          </span>
          {hasData && (
            <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-teal-500/15 text-teal-400 border border-teal-500/20 font-bold">
              Live
            </span>
          )}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3 space-y-3.5">
              {PERMIT_LIMITS.map(l => {
                const measured = avgs[l.key] ?? null;

                // Compliance + bar math
                let pct: number | null = null;
                let exceeded = false;
                let withinRange = true;

                if (measured !== null) {
                  if (l.isRange) {
                    const low = l.limitLow ?? 0;
                    exceeded = measured < low || measured > l.limitHigh;
                    withinRange = !exceeded;
                    // position within 0 → limitHigh*1.2 scale for bar display
                    pct = (measured / (l.limitHigh * 1.2)) * 100;
                  } else {
                    pct = (measured / l.limitHigh) * 100;
                    exceeded = measured > l.limitHigh;
                  }
                }

                // Bar fill color based on compliance level
                const fillColor = exceeded
                  ? '#ef4444'
                  : pct !== null && pct > 85
                    ? '#f97316'
                    : pct !== null && pct > 65
                      ? '#f59e0b'
                      : '#14b8a6';

                const displayVal = measured !== null
                  ? (l.isRange ? measured.toFixed(1) : fmtNum(measured, measured < 10 ? 2 : 1))
                  : null;

                return (
                  <div key={l.label}>
                    {/* Label row */}
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                        <span className="text-[10px] font-bold text-slate-300">{l.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {displayVal !== null && (
                          <span
                            className="text-[9px] font-mono font-black px-1.5 py-0.5 rounded"
                            style={{
                              color: exceeded ? '#f87171' : '#2dd4bf',
                              background: exceeded ? 'rgba(239,68,68,0.12)' : 'rgba(20,184,166,0.12)',
                            }}
                          >
                            {displayVal}{l.unit ? ` ${l.unit}` : ''}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400 font-mono font-bold">
                          {l.range}{l.unit ? ` ${l.unit}` : ''}
                        </span>
                      </div>
                    </div>

                    {/* Horizontal bar track */}
                    <div className="relative h-2 rounded-full bg-slate-700/50 overflow-visible">
                      {/* Limit marker line at 100% of limit */}
                      {!l.isRange && (
                        <div
                          className="absolute top-[-2px] h-[calc(100%+4px)] w-px z-10"
                          style={{
                            left: `${Math.min((l.limitHigh / (l.limitHigh * 1.25)) * 100, 100)}%`,
                            backgroundColor: 'rgba(148,163,184,0.4)',
                          }}
                        />
                      )}
                      {/* Range band for pH */}
                      {l.isRange && l.limitLow !== null && (
                        <div
                          className="absolute top-0 h-full rounded-full opacity-20"
                          style={{
                            left:  `${(l.limitLow  / (l.limitHigh * 1.2)) * 100}%`,
                            right: `${100 - (l.limitHigh / (l.limitHigh * 1.2)) * 100}%`,
                            backgroundColor: l.color,
                          }}
                        />
                      )}
                      {/* Measured fill */}
                      {pct !== null && (
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(Math.max(pct, 1.5), 108)}%` }}
                          transition={{ duration: 0.55, ease: 'easeOut', delay: 0.05 }}
                          className="absolute left-0 top-0 h-full rounded-full"
                          style={{ backgroundColor: fillColor, opacity: 0.85 }}
                        />
                      )}
                      {/* No data stripe */}
                      {measured === null && (
                        <div className="h-full w-full rounded-full bg-slate-700/20" />
                      )}
                    </div>

                    {/* Below-bar annotation */}
                    <div className="flex items-center justify-between mt-0.5 px-0.5">
                      <span className="text-[8px] text-slate-400">0</span>
                      {measured === null ? (
                        <span className="text-[8px] text-slate-600 italic">no sample data</span>
                      ) : exceeded ? (
                        <span className="text-[8px] text-red-500 font-black tracking-wide">⚠ EXCEEDS LIMIT</span>
                      ) : pct !== null && pct > 85 ? (
                        <span className="text-[8px] text-orange-500 font-bold">⚡ Near limit ({Math.round(pct)}%)</span>
                      ) : pct !== null && pct > 65 ? (
                        <span className="text-[8px] text-amber-500 font-bold">{Math.round(pct)}% of limit</span>
                      ) : pct !== null ? (
                        <span className="text-[8px] text-teal-600 font-bold">✓ {Math.round(pct)}% of limit</span>
                      ) : null}
                      <span className="text-[8px] text-slate-400">{l.range} {l.unit}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-4 pb-3 pt-1 border-t border-slate-800/40">
              <p className="text-[9px] text-slate-600 leading-snug">
                Sewerage Act 1929 (SA) — SA Water Trade Waste Regulator
              </p>
              <a
                href="https://www.sawater.com.au/business-and-industry/trade-waste"
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-[9px] text-teal-600 hover:text-teal-400 mt-1 transition-colors"
              >
                <ExternalLink className="w-2 h-2" />SA Water Trade Waste
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Charge Rate panel ────────────────────────────────────────────────────────
function ChargeRatePanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-800/60 bg-slate-800/40 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <Receipt className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Charge Rate Schedule
          </span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b border-slate-800/40">
                    <th className="text-left py-2 text-slate-500 font-bold uppercase tracking-wider">Param</th>
                    <th className="text-right py-2 px-2 text-slate-500 font-bold uppercase tracking-wider">Condition</th>
                    <th className="text-right py-2 text-slate-500 font-bold uppercase tracking-wider">$/kg excl. GST</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                  <tr><td className="py-1.5 text-slate-300 font-bold">BOD₅</td><td className="px-2 text-right text-slate-500">≤ 1,000 mg/L</td><td className="text-right font-mono text-emerald-400 font-bold">$0.251</td></tr>
                  <tr><td className="py-1.5 text-slate-300 font-bold">BOD₅</td><td className="px-2 text-right text-slate-500">&gt; 1,000 mg/L</td><td className="text-right font-mono text-amber-400 font-bold">$0.378</td></tr>
                  <tr><td className="py-1.5 text-slate-300 font-bold">SS</td><td className="px-2 text-right text-slate-500">≤ 500 mg/L</td><td className="text-right font-mono text-emerald-400 font-bold">$0.218</td></tr>
                  <tr><td className="py-1.5 text-slate-300 font-bold">SS</td><td className="px-2 text-right text-slate-500">&gt; 500 mg/L</td><td className="text-right font-mono text-amber-400 font-bold">$0.315</td></tr>
                  <tr><td className="py-1.5 text-slate-300 font-bold">TDS</td><td className="px-2 text-right text-slate-500">All</td><td className="text-right font-mono text-slate-300">$0.126</td></tr>
                  <tr><td className="py-1.5 text-slate-300 font-bold">TKN</td><td className="px-2 text-right text-slate-500">Avg &gt; 650 mg/L</td><td className="text-right font-mono text-amber-400 font-bold">$0.392</td></tr>
                  <tr><td className="py-1.5 text-slate-300 font-bold">TP</td><td className="px-2 text-right text-slate-500">All</td><td className="text-right font-mono text-red-400 font-bold">$1.908</td></tr>
                </tbody>
              </table>
              <p className="text-[9px] text-slate-600 pt-2 mt-2 border-t border-slate-800/40">
                Triggers: &gt;10 t/yr BOD or SS · &gt;20 t/yr TDS · OR &gt;10,000 kL/yr volume
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SAWaterChargePage() {
  const { currentCustomer } = useSession();

  const [sites, setSites]               = useState<ApiSite[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);

  const [allSamples, setAllSamples]         = useState<ApiSample[]>([]);
  const [loadingSamples, setLoadingSamples] = useState(false);

  const [flowM3h, setFlowM3h]           = useState<number>(5);
  const [operatingDays, setOperatingDays] = useState<number>(250);
  const [activePeriod, setActivePeriod] = useState<'monthly' | 'quarterly' | 'annual'>('annual');
  const [selectedSampleIds, setSelectedSampleIds] = useState<Set<number>>(new Set());

  const customerId = currentCustomer?.serverId
    ?? (currentCustomer ? parseInt(currentCustomer.id) : null);

  // Load SA Water sites — reset sample count when site changes
  useEffect(() => {
    if (!customerId || !hasAuthToken()) { setSites([]); setSelectedSiteId(null); return; }
    setLoadingSites(true);
    apiListCustomerSites(customerId)
      .then((data: ApiSite[]) => {
        const saw = data.filter(s => s.standard === 'SA_WATER' || s.standard === 'sa_water');
        setSites(saw);
        if (saw.length === 1) setSelectedSiteId(saw[0].id);
        else setSelectedSiteId(null);
      })
      .catch(console.error)
      .finally(() => setLoadingSites(false));
  }, [customerId]);

  // Load EFFLUENT samples only — SA Water charges are calculated on discharge/effluent data
  useEffect(() => {
    if (!customerId || !hasAuthToken()) { setAllSamples([]); return; }
    setLoadingSamples(true);
    Promise.all([
      // Primary: influent_samples table filtered to type=effluent (camelCase fields, has reportNumber)
      apiListInfluentSamples(customerId, { type: 'effluent' }).catch(() => [] as any[]),
      // Legacy: customer_samples table — filter client-side to effluent only
      apiListCustomerSamples(customerId).catch(() => [] as any[]),
    ]).then(([influentRows, customerRows]) => {
      const seen = new Set<number>();
      const merged: ApiSample[] = [];
      for (const s of [...(influentRows || []), ...(customerRows || [])]) {
        const id = Number(s.id);
        if (seen.has(id)) continue;
        // Exclude samples explicitly typed as influent — SA Water only uses effluent data
        const typ = (s.sampleType ?? s.sample_type ?? '').toLowerCase();
        if (typ === 'influent') continue;
        seen.add(id);
        merged.push(s);
      }
      setAllSamples(merged);
    }).finally(() => setLoadingSamples(false));
  }, [customerId]);

  const selectedSite = sites.find(s => s.id === selectedSiteId) ?? null;

  // Effluent samples for the selected SA Water site only — no cross-site fallback.
  // SA Water charges must reflect samples linked to this specific discharge point.
  // Handles both camelCase (influent_samples) and snake_case (customer_samples) field names.
  const siteSamples = useMemo(() => {
    if (!selectedSiteId) return [];
    const sid = String(selectedSiteId);
    return allSamples.filter(s => {
      const raw = s.siteId ?? s.site_id;
      return raw !== null && raw !== undefined && String(raw) === sid;
    });
  }, [allSamples, selectedSiteId]);

  // When site's samples change, auto-select all of them
  useEffect(() => {
    setSelectedSampleIds(new Set(siteSamples.map(s => Number(s.id))));
  }, [siteSamples]);

  const toggleSample = useCallback((id: number) => {
    setSelectedSampleIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllSamples = useCallback(() => {
    setSelectedSampleIds(new Set(siteSamples.map(s => Number(s.id))));
  }, [siteSamples]);

  const clearAllSamples = useCallback(() => {
    setSelectedSampleIds(new Set());
  }, []);

  // Active samples = user's ticked selection from site-linked samples only
  const activeSamples = useMemo(() => {
    return siteSamples.filter(s => selectedSampleIds.has(Number(s.id)));
  }, [siteSamples, selectedSampleIds]);

  const annualKL       = flowM3h * 24 * operatingDays;
  const volumeTriggered = annualKL >= 10000;

  // Average concentrations (charge params + permit limit params) over active samples
  const avgs = useMemo<Record<string, number | null>>(() => {
    const result: Record<string, number | null> = {};
    const keys = [...SA_WATER_RATES.map(r => r.key), 'ph', 'temperature', 'oilGrease'];
    for (const key of keys) {
      const vals = activeSamples
        .map(s => extractValue(parseParams(s.params), key))
        .filter((v): v is number => v !== null && v >= 0);
      result[key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    }
    return result;
  }, [activeSamples]);

  // Charge rows
  const chargeRows = useMemo<ChargeRow[]>(() => {
    return SA_WATER_RATES.map(r => {
      const avg = avgs[r.key] ?? null;
      const loadKg = avg !== null && annualKL > 0 ? avg * annualKL * 0.001 : null;
      const highRateApplied = r.threshold !== null && avg !== null && avg > r.threshold;
      const rate = highRateApplied ? r.highRate : r.lowRate;
      const triggered = volumeTriggered
        || (r.triggerKg !== null && loadKg !== null && loadKg > r.triggerKg);
      const exceeds = r.tknCondition ? (avg !== null && avg > 650) : true;
      const charge = triggered && exceeds && loadKg !== null ? loadKg * rate : null;
      return {
        key: r.key, label: r.label, fullName: r.fullName, color: r.color,
        avgMgL: avg, loadKg, rate, charge, triggered, exceeds, highRateApplied,
      };
    });
  }, [avgs, annualKL, volumeTriggered]);

  const totalAnnual   = chargeRows.reduce((s, r) => s + (r.charge ?? 0), 0);
  const anyCharged    = chargeRows.some(r => r.charge !== null && r.charge > 0);
  const periodDiv     = activePeriod === 'annual' ? 1 : activePeriod === 'quarterly' ? 4 : 12;
  const periodTotal   = totalAnnual / periodDiv;
  const periodLabel   = activePeriod === 'annual' ? 'yr' : activePeriod === 'quarterly' ? 'qtr' : 'mo';

  const chartData = chargeRows
    .filter(r => r.charge !== null && r.charge > 0)
    .map(r => ({
      label: r.label, fullName: r.fullName,
      value: (r.charge ?? 0) / periodDiv,
      color: r.color,
    }));

  const customerName = currentCustomer
    ? (currentCustomer.company || currentCustomer.name) : null;

  const { user } = useAuth();
  const preparedBy = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : (user?.name || user?.username || undefined);

  // ─── Saved estimates state ─────────────────────────────────────────────────
  const [saving, setSaving]               = useState(false);
  const [saveMsg, setSaveMsg]             = useState<{ ok: boolean; text: string } | null>(null);
  const [savedEstimates, setSavedEstimates] = useState<any[]>([]);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [showEstimates, setShowEstimates] = useState(false);

  // Load saved estimates whenever customer / site changes
  useEffect(() => {
    if (!customerId || !selectedSiteId || !hasAuthToken()) { setSavedEstimates([]); return; }
    setLoadingEstimates(true);
    apiListSAWaterEstimates(customerId, selectedSiteId)
      .then(setSavedEstimates)
      .catch(console.error)
      .finally(() => setLoadingEstimates(false));
  }, [customerId, selectedSiteId]);

  const refreshEstimates = useCallback(() => {
    if (!customerId || !selectedSiteId || !hasAuthToken()) return;
    apiListSAWaterEstimates(customerId, selectedSiteId).then(setSavedEstimates).catch(() => {});
  }, [customerId, selectedSiteId]);

  // ─── Generate sample label for reports / saves ─────────────────────────────
  const sampleLabel = (() => {
    if (activeSamples.length === 0) return 'No samples selected';
    if (activeSamples.length === 1) {
      const s = activeSamples[0];
      const ref = s.reportNumber || s.report_number || s.sampleName || s.sample_name;
      return ref ? `Single sample — ${ref}` : 'Single sample';
    }
    return `${activeSamples.length} samples averaged`;
  })();

  // ─── Save estimate to database ─────────────────────────────────────────────
  const handleSaveEstimate = async () => {
    if (!customerId || !selectedSiteId || annualKL <= 0) return;
    setSaving(true);
    setSaveMsg(null);
    const refNum = buildRegulatoryReportNumber('EFF', 'SA_WATER');
    try {
      await apiSaveSAWaterEstimate({
        customer_id: customerId,
        site_id: selectedSiteId,
        reference_number: refNum,
        sample_count: activeSamples.length,
        annual_kl: annualKL,
        flow_m3h: flowM3h,
        operating_days: operatingDays,
        total_charge: totalAnnual,
        sample_label: sampleLabel,
        estimate_data: {
          customerName: customerName || '',
          siteName: selectedSite?.name || '',
          siteAddress: selectedSite?.address || '',
          sitePostcode: selectedSite?.postcode || '',
          permitRef: selectedSite?.permitRef || selectedSite?.permit_ref || '',
          preparedBy: preparedBy || '',
          volumeMethod: 'flowrate',
          chargeRows: chargeRows.map(r => ({
            label: r.label, fullName: r.fullName,
            avg: r.avgMgL, loadKg: r.loadKg,
            rate: r.rate, charge: r.charge,
            triggered: r.triggered, exceeds: r.exceeds,
            highRateApplied: r.highRateApplied,
          })),
        },
      });
      setSaveMsg({ ok: true, text: `Saved — ${refNum}` });
      refreshEstimates();
      setShowEstimates(true);
    } catch (e: any) {
      setSaveMsg({ ok: false, text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 5000);
    }
  };

  // ─── Download PDF for current calculation ─────────────────────────────────
  const handleDownloadPDF = (refNum?: string) => {
    const ref = refNum || buildRegulatoryReportNumber('EFF', 'SA_WATER');
    const pdfRows: SAWaterChargeRow[] = chargeRows.map(r => ({
      label: r.label, fullName: r.fullName,
      avg: r.avgMgL, loadKg: r.loadKg,
      rate: r.rate, charge: r.charge,
      triggered: r.triggered, exceeds: r.exceeds,
      highRateApplied: r.highRateApplied,
    }));
    generateSAWaterChargesReport({
      customerCompany: customerName || 'Unknown',
      siteName: selectedSite?.name || 'Unknown',
      siteAddress: selectedSite?.address || '',
      sitePostcode: selectedSite?.postcode,
      permitRef: selectedSite?.permitRef || selectedSite?.permit_ref,
      sampleLabel,
      annualKL,
      flowRateM3day: flowM3h * 24,
      operatingDays,
      volumeMethod: 'flowrate',
      rows: pdfRows,
      totalCharge: totalAnnual,
      referenceNumber: ref,
      preparedBy,
      mode: 'download',
    });
  };

  // ─── Download PDF for a saved estimate ───────────────────────────────────
  const handleDownloadSavedPDF = (est: any) => {
    const data = est.estimate_data as any;
    const rows: SAWaterChargeRow[] = (data?.chargeRows || []).map((r: any) => ({
      label: r.label, fullName: r.fullName,
      avg: r.avg ?? null, loadKg: r.loadKg ?? null,
      rate: r.rate ?? 0, charge: r.charge ?? null,
      triggered: !!r.triggered, exceeds: !!r.exceeds,
      highRateApplied: !!r.highRateApplied,
    }));
    generateSAWaterChargesReport({
      customerCompany: customerName || 'Unknown',
      siteName: selectedSite?.name || 'Unknown',
      siteAddress: selectedSite?.address || '',
      sitePostcode: selectedSite?.postcode,
      permitRef: selectedSite?.permitRef || selectedSite?.permit_ref,
      sampleLabel: est.sample_label || sampleLabel,
      annualKL: Number(est.annual_kl) || annualKL,
      flowRateM3day: est.flow_m3h ? Number(est.flow_m3h) * 24 : flowM3h * 24,
      operatingDays: Number(est.operating_days) || operatingDays,
      volumeMethod: 'flowrate',
      rows,
      totalCharge: Number(est.total_charge),
      referenceNumber: est.reference_number,
      preparedBy,
      mode: 'download',
    });
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Page Header */}
      <PageHeader
        icon={Receipt}
        iconColor="text-teal-400"
        title="SA Water — Trade Waste Charges"
        subtitle="Estimate excess concentration charges under the SA Water RWAS schedule (AUD excl. GST)"
        badge={
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-300 border border-teal-500/20 font-bold">
            🇦🇺 South Australia
          </span>
        }
        actions={
          <a
            href="https://www.sawater.com.au/business-and-industry/trade-waste"
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-teal-400 border border-teal-500/25 bg-teal-500/10 hover:bg-teal-500/20 transition-all"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            SA Water Website
          </a>
        }
      />

      {/* Active Customer Banner */}
      {currentCustomer ? (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-teal-500/30 bg-teal-500/8 overflow-hidden"
          style={{ background: 'rgba(20,184,166,0.06)' }}
        >
          <div className="flex items-center justify-between px-5 py-3.5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-teal-500/20 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-teal-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[14px] font-black text-slate-100">{customerName}</p>
                  <span className="px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-400 text-[9px] font-bold uppercase tracking-wide border border-teal-500/20">
                    Active Session
                  </span>
                </div>
                {selectedSite ? (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <ChevronRight className="w-3 h-3 text-slate-500" />
                    <p className="text-[11px] text-teal-400 font-bold">{selectedSite.name}</p>
                  </div>
                ) : (
                  <p className="text-[10px] text-slate-500 mt-0.5">Select a site below to begin calculations</p>
                )}
              </div>
            </div>
            <div className="text-right">
              {sites.length > 0 && (
                <p className="text-[10px] text-slate-500">
                  {sites.length} SA Water site{sites.length !== 1 ? 's' : ''}
                </p>
              )}
              {siteSamples.length > 0 && (
                <p className="text-[10px] text-teal-500 font-bold mt-0.5">
                  {activeSamples.length}/{siteSamples.length} selected
                </p>
              )}
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-700 bg-slate-800/30 p-10 text-center">
          <Building2 className="w-9 h-9 text-slate-400 mx-auto mb-3" />
          <p className="text-[13px] font-bold text-slate-500">No Active Customer</p>
          <p className="text-[11px] text-slate-600 mt-1">
            Select a customer in the Customers page to use this calculator.
          </p>
        </div>
      )}

      {currentCustomer && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── LEFT: Sites + Reference panels ──────────────────────────────── */}
          <div className="space-y-3">

            <div className="flex items-center gap-2 px-1">
              <div className="w-4 h-4 rounded bg-teal-500/25 flex items-center justify-center">
                <MapPin className="w-2.5 h-2.5 text-teal-400" />
              </div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                SA Water Sites
              </span>
              {loadingSites && (
                <RefreshCw className="w-3 h-3 text-slate-600 animate-spin ml-auto" />
              )}
            </div>

            {loadingSites ? (
              <div className="space-y-2">
                {[1, 2].map(i => (
                  <div key={i} className="rounded-xl border border-slate-800/40 bg-slate-800/40 p-3.5 animate-pulse">
                    <div className="w-8 h-8 rounded-lg bg-slate-700/60 mb-2.5" />
                    <div className="h-2.5 bg-slate-700/60 rounded w-3/4 mb-1.5" />
                    <div className="h-2 bg-slate-700/40 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : sites.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-800/60 bg-slate-800/20 p-5 text-center">
                <Building2 className="w-7 h-7 text-slate-400 mx-auto mb-2" />
                <p className="text-[11px] font-bold text-slate-500">No SA Water sites</p>
                <p className="text-[10px] text-slate-600 mt-1">
                  Add sites with SA Water standard in Customers.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {sites.map(site => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    selected={selectedSiteId === site.id}
                    onClick={() => setSelectedSiteId(site.id === selectedSiteId ? null : site.id)}
                  />
                ))}
              </div>
            )}

            <PermitLimitsPanel avgs={avgs} />
            <ChargeRatePanel />

            {/* Trigger info card */}
            <div className="rounded-xl border border-blue-500/20 bg-blue-950/20 p-3.5">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">
                  Charge Triggers
                </span>
              </div>
              <div className="space-y-1">
                {[
                  'BOD > 10,000 kg/yr',
                  'SS > 10,000 kg/yr',
                  'TDS > 20,000 kg/yr',
                  'Volume > 10,000 kL/yr',
                ].map(t => (
                  <div key={t} className="flex items-center gap-1.5 text-[10px] text-blue-400/80">
                    <div className="w-1 h-1 rounded-full bg-blue-500 shrink-0" />
                    {t}
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-blue-600 mt-2">
                Any single trigger activates charges for that parameter
              </p>
            </div>
          </div>

          {/* ── RIGHT: Calculator + Results ──────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-4">

            <AnimatePresence mode="wait">
              {!selectedSiteId ? (
                <motion.div
                  key="no-site"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="rounded-xl border border-dashed border-slate-800/60 bg-slate-800/20 p-16 text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-800/60 flex items-center justify-center mx-auto mb-4">
                    <Calculator className="w-5 h-5 text-slate-600" />
                  </div>
                  <p className="text-[13px] font-bold text-slate-500">Select a site to begin</p>
                  <p className="text-[10px] text-slate-600 mt-1">
                    Choose an SA Water site from the list on the left
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key={`site-${selectedSiteId}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4"
                >

                  {/* Flow Rate & Volume */}
                  <div className="rounded-xl border border-slate-800/60 bg-slate-800/40 overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-800/40 bg-slate-900/40">
                      <Gauge className="w-4 h-4 text-teal-500" />
                      <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                        Flow Rate &amp; Annual Volume
                      </span>
                    </div>

                    <div className="p-5 space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                        {/* Flow rate */}
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                            Flow Rate (m³/h)
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={flowM3h}
                              min={0.1} max={500} step={0.5}
                              onChange={e => setFlowM3h(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                              className="w-full pl-4 pr-16 py-3 rounded-lg border border-slate-600/60 bg-rb-surface text-slate-100 text-[15px] font-black font-mono focus:outline-none focus:border-teal-500/60 transition-all"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 font-bold">m³/h</span>
                          </div>
                          <input
                            type="range" min={0.5} max={100} step={0.5}
                            value={Math.min(flowM3h, 100)}
                            onChange={e => setFlowM3h(parseFloat(e.target.value))}
                            className="w-full mt-2 accent-teal-500"
                          />
                          <div className="flex justify-between text-[9px] text-slate-600">
                            <span>0.5</span><span>100 m³/h</span>
                          </div>
                        </div>

                        {/* Operating days */}
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                            Operating Days / Year
                          </label>
                          <div className="w-full px-4 py-3 rounded-lg border border-slate-800/60 bg-slate-900/40 text-slate-100 text-[15px] font-black font-mono">
                            {operatingDays}
                            <span className="text-[10px] font-normal text-slate-500 ml-1">days/yr</span>
                          </div>
                          <input
                            type="range" min={50} max={365} step={5}
                            value={operatingDays}
                            onChange={e => setOperatingDays(parseInt(e.target.value))}
                            className="w-full mt-2 accent-teal-500"
                          />
                          <div className="flex justify-between text-[9px] text-slate-600">
                            <span>50</span><span>365 days</span>
                          </div>
                        </div>
                      </div>

                      {/* kL/yr result pill */}
                      <div className={`flex items-center gap-3 px-4 py-3.5 rounded-lg border ${
                        volumeTriggered
                          ? 'border-red-500/30 bg-red-500/8'
                          : 'border-teal-500/25 bg-teal-500/8'
                      }`}
                        style={{ background: volumeTriggered ? 'rgba(239,68,68,0.06)' : 'rgba(20,184,166,0.06)' }}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          volumeTriggered ? 'bg-red-500/20' : 'bg-teal-500/20'
                        }`}>
                          <Droplets className={`w-4 h-4 ${volumeTriggered ? 'text-red-400' : 'text-teal-400'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className={`text-[22px] font-black tabular-nums ${
                              volumeTriggered ? 'text-red-400' : 'text-teal-300'
                            }`}>
                              {fmtNum(annualKL, 0)}
                            </span>
                            <span className="text-[11px] text-slate-400 font-bold">kL/yr</span>
                            <span className="text-[10px] text-slate-600">
                              ({fmtNum(flowM3h, 1)} m³/h × 24h × {operatingDays}d)
                            </span>
                          </div>
                          <p className={`text-[10px] font-bold mt-0.5 ${
                            volumeTriggered ? 'text-red-500' : 'text-teal-500'
                          }`}>
                            {volumeTriggered
                              ? '⚠ Volume trigger exceeded — all parameters chargeable'
                              : '✓ Below 10,000 kL/yr volume trigger'
                            }
                          </p>
                        </div>
                      </div>

                      {/* Sample data — count selector + reference list */}
                      {loadingSamples ? (
                        <div className="flex items-center gap-2 text-[10px] text-slate-500">
                          <RefreshCw className="w-3 h-3 animate-spin" />Loading samples…
                        </div>
                      ) : siteSamples.length === 0 ? (
                        <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-lg border border-amber-500/25 bg-amber-500/5">
                          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[11px] font-bold text-amber-400">
                              No effluent samples linked to this site
                            </p>
                            <p className="text-[10px] text-slate-500 mt-0.5">
                              When adding effluent samples, select <strong className="text-slate-400">{selectedSite?.name}</strong> as the site so they appear here.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-teal-500/20 bg-teal-500/5 p-3 space-y-2">
                          {/* Header */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                              <span className="text-[10px] font-bold text-teal-300">
                                {activeSamples.length} of {siteSamples.length} sample{siteSamples.length !== 1 ? 's' : ''} active
                              </span>
                              {activeSamples.length === 1 && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-teal-500/20 text-teal-300 border border-teal-400/25 font-bold">Single</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={selectAllSamples}
                                className="px-2 py-0.5 rounded text-[9px] font-bold text-teal-400 border border-teal-500/25 bg-teal-500/10 hover:bg-teal-500/20 transition-all"
                              >All</button>
                              <button
                                onClick={clearAllSamples}
                                className="px-2 py-0.5 rounded text-[9px] font-bold text-slate-500 border border-slate-800/50 bg-slate-800/40 hover:text-slate-300 transition-all"
                              >None</button>
                            </div>
                          </div>

                          {/* Per-sample toggle rows */}
                          <div className="space-y-1 max-h-[230px] overflow-y-auto scrollbar-thin pr-0.5">
                            {siteSamples.map(s => {
                              const isSelected = selectedSampleIds.has(Number(s.id));
                              const p = parseParams(s.params);
                              const bod = extractValue(p, 'bod5');
                              const tss = extractValue(p, 'tss');
                              const tds = extractValue(p, 'tds');
                              const tn  = extractValue(p, 'tn');
                              const ref  = s.reportNumber || s.report_number;
                              const name = s.sampleName   || s.sample_name;
                              const rawDate = s.createdAt || s.created_at || s.sample_date;
                              return (
                                <button
                                  key={s.id}
                                  onClick={() => toggleSample(Number(s.id))}
                                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border text-left transition-all duration-150 ${
                                    isSelected
                                      ? 'bg-teal-500/10 border-teal-400/25 hover:border-teal-400/40'
                                      : 'bg-slate-800/60 border-slate-800/40 opacity-50 hover:opacity-70'
                                  }`}
                                >
                                  {/* Checkbox */}
                                  <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border transition-colors ${
                                    isSelected ? 'bg-teal-500 border-teal-400' : 'bg-slate-700 border-slate-600'
                                  }`}>
                                    {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                                  </div>

                                  {/* Label + date */}
                                  <div className="min-w-0 flex-1">
                                    <span className={`block text-[10px] font-mono font-bold truncate ${
                                      isSelected ? 'text-teal-300' : 'text-slate-500'
                                    }`}>
                                      {ref || name || `Sample #${s.id}`}
                                    </span>
                                    {rawDate && (
                                      <span className="text-[8px] text-slate-600">
                                        {new Date(rawDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })}
                                      </span>
                                    )}
                                  </div>

                                  {/* Key params inline */}
                                  <div className="flex items-center gap-2 shrink-0 text-[8px] font-mono">
                                    {bod !== null && (
                                      <span className="text-slate-500">BOD <span className="font-bold" style={{ color: '#f97316' }}>{fmtNum(bod, 0)}</span></span>
                                    )}
                                    {tss !== null && (
                                      <span className="text-slate-500">SS <span className="font-bold" style={{ color: '#eab308' }}>{fmtNum(tss, 0)}</span></span>
                                    )}
                                    {tds !== null && (
                                      <span className="hidden sm:inline text-slate-500">TDS <span className="font-bold" style={{ color: '#3b82f6' }}>{fmtNum(tds, 0)}</span></span>
                                    )}
                                    {tn !== null && (
                                      <span className="hidden md:inline text-slate-500">TKN <span className="font-bold" style={{ color: '#8b5cf6' }}>{fmtNum(tn, 0)}</span></span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {activeSamples.length === 0 && (
                            <div className="flex items-center gap-1.5 text-[10px] text-amber-500 pt-0.5">
                              <AlertTriangle className="w-3 h-3 shrink-0" />
                              No samples selected — select at least one to calculate charges
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Charges Table */}
                  {annualKL > 0 && (
                    <div className="rounded-xl border border-slate-800/60 bg-slate-800/40 overflow-hidden">
                      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800/40 bg-slate-900/40">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-slate-400" />
                          <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                            Excess Concentration Charges
                          </span>
                        </div>
                        <span className="text-[9px] text-slate-600">
                          {fmtNum(annualKL, 0)} kL/yr
                          {activeSamples.length === 1 && ' · single sample'}
                          {activeSamples.length > 1 && ` · ${activeSamples.length}-sample avg`}
                        </span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead className="border-b border-slate-800/40 bg-slate-900/30">
                            <tr>
                              <th className="text-left px-5 py-3 text-slate-500 font-bold uppercase tracking-wider">Parameter</th>
                              <th className="text-right px-3 py-3 text-slate-500 font-bold uppercase tracking-wider">
                                Avg<br/><span className="text-slate-600 font-normal normal-case text-[9px]">mg/L</span>
                              </th>
                              <th className="text-right px-3 py-3 text-slate-500 font-bold uppercase tracking-wider">
                                Load<br/><span className="text-slate-600 font-normal normal-case text-[9px]">kg/yr</span>
                              </th>
                              <th className="text-right px-3 py-3 text-slate-500 font-bold uppercase tracking-wider">
                                Rate<br/><span className="text-slate-600 font-normal normal-case text-[9px]">$/kg</span>
                              </th>
                              <th className="text-right px-5 py-3 text-slate-500 font-bold uppercase tracking-wider">
                                Annual Charge
                              </th>
                              <th className="text-center px-3 py-3 text-slate-500 font-bold uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-700/30">
                            {chargeRows.map(r => {
                              const noData  = r.avgMgL === null;
                              const charged = r.charge !== null && r.charge > 0;
                              return (
                                <tr key={r.key} className={`transition-colors ${
                                  charged ? 'bg-red-500/5 hover:bg-red-500/8'
                                  : 'hover:bg-slate-700/20'
                                }`}>
                                  <td className="px-5 py-3.5">
                                    <div className="flex items-center gap-2.5">
                                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.color }} />
                                      <div>
                                        <p className={`font-black text-[12px] ${
                                          charged ? 'text-red-300' : noData ? 'text-slate-600' : 'text-slate-200'
                                        }`}>{r.label}</p>
                                        <p className="text-[9px] text-slate-600">{r.fullName}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-right px-3 py-3.5">
                                    <span className={`font-mono font-bold text-[12px] ${
                                      noData ? 'text-slate-400'
                                      : r.highRateApplied ? 'text-amber-400'
                                      : 'text-slate-300'
                                    }`}>
                                      {noData ? '—' : fmtNum(r.avgMgL!, r.avgMgL! < 10 ? 2 : 1)}
                                    </span>
                                    {r.highRateApplied && (
                                      <span className="text-[8px] text-amber-500 ml-0.5">▲</span>
                                    )}
                                  </td>
                                  <td className="text-right px-3 py-3.5">
                                    <span className={`font-mono font-bold text-[12px] ${
                                      !r.loadKg ? 'text-slate-400'
                                      : r.triggered ? 'text-amber-400'
                                      : 'text-slate-500'
                                    }`}>
                                      {!r.loadKg ? '—' : fmtNum(r.loadKg, 0)}
                                    </span>
                                  </td>
                                  <td className="text-right px-3 py-3.5">
                                    <span className={`font-mono text-[11px] ${
                                      r.highRateApplied ? 'text-amber-400 font-bold' : 'text-slate-500'
                                    }`}>
                                      {noData ? '—' : `$${r.rate.toFixed(3)}`}
                                    </span>
                                  </td>
                                  <td className="text-right px-5 py-3.5">
                                    {noData ? (
                                      <span className="text-slate-400 text-[11px]">—</span>
                                    ) : !r.triggered ? (
                                      <span className="text-[10px] text-emerald-500 font-bold">Below trigger</span>
                                    ) : !r.exceeds ? (
                                      <span className="text-[10px] text-slate-500 font-bold">≤ 650 mg/L</span>
                                    ) : (
                                      <span className={`font-black font-mono text-[13px] tabular-nums ${
                                        r.charge! > 10000 ? 'text-red-400'
                                        : r.charge! > 2000 ? 'text-amber-400'
                                        : 'text-yellow-400'
                                      }`}>
                                        {fmtAUD(r.charge!)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-center px-3 py-3.5">
                                    {noData ? (
                                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-600 font-bold">No data</span>
                                    ) : charged ? (
                                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/25 font-bold">Charged</span>
                                    ) : !r.triggered ? (
                                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 font-bold">OK</span>
                                    ) : (
                                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-slate-700/60 text-slate-500 font-bold">N/A</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Period Breakdown + Chart */}
                  {annualKL > 0 && anyCharged && (
                    <div className="rounded-xl border border-slate-800/60 bg-slate-800/40 overflow-hidden">
                      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800/40 bg-slate-900/40">
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-slate-400" />
                          <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                            Charge Breakdown by Period
                          </span>
                        </div>
                        <div className="flex rounded-lg overflow-hidden border border-slate-800/60">
                          {(['monthly', 'quarterly', 'annual'] as const).map(p => (
                            <button
                              key={p}
                              onClick={() => setActivePeriod(p)}
                              className={`px-3 py-1.5 text-[10px] font-bold transition-all ${
                                activePeriod === p
                                  ? 'bg-teal-500/25 text-teal-300 border-x border-teal-500/30'
                                  : 'bg-slate-800/40 text-slate-500 hover:text-slate-300'
                              }`}
                            >
                              {p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="p-5 space-y-5">
                        {/* Period breakdown table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]">
                            <thead className="border-b border-slate-800/40">
                              <tr>
                                <th className="text-left py-2 text-slate-500 font-bold uppercase tracking-wider">Period</th>
                                {chargeRows.filter(r => r.charge !== null && r.charge > 0).map(r => (
                                  <th key={r.key} className="text-right px-2 py-2 text-slate-500 font-bold uppercase tracking-wider">
                                    {r.label}
                                  </th>
                                ))}
                                <th className="text-right py-2 text-slate-400 font-bold uppercase tracking-wider">Total</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/25">
                              {([
                                { label: 'Monthly',   div: 12 },
                                { label: 'Quarterly', div: 4  },
                                { label: 'Annual',    div: 1  },
                              ] as const).map(({ label, div }) => {
                                const active =
                                  (div === 1  && activePeriod === 'annual') ||
                                  (div === 4  && activePeriod === 'quarterly') ||
                                  (div === 12 && activePeriod === 'monthly');
                                return (
                                  <tr key={label} className={`transition-colors ${
                                    active ? 'bg-teal-500/8' : 'hover:bg-slate-700/20'
                                  }`}
                                    style={active ? { background: 'rgba(20,184,166,0.06)' } : {}}
                                  >
                                    <td className={`py-3 font-bold ${active ? 'text-teal-300' : 'text-slate-400'}`}>
                                      {label}
                                    </td>
                                    {chargeRows.filter(r => r.charge !== null && r.charge > 0).map(r => (
                                      <td key={r.key} className={`text-right px-2 py-3 font-mono tabular-nums ${
                                        active ? 'text-slate-300' : 'text-slate-500'
                                      }`}>
                                        {fmtAUD((r.charge ?? 0) / div)}
                                      </td>
                                    ))}
                                    <td className={`text-right py-3 font-mono font-black tabular-nums ${
                                      active ? 'text-teal-300' : 'text-slate-300'
                                    }`}>
                                      {fmtAUD(totalAnnual / div)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Bar chart */}
                        {chartData.length > 0 && (
                          <div>
                            <p className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mb-3">
                              Charge Distribution — {activePeriod.charAt(0).toUpperCase() + activePeriod.slice(1)}
                            </p>
                            <ResponsiveContainer width="100%" height={170}>
                              <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.15)" vertical={false} />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fontSize: 11, fontWeight: 700, fill: 'var(--rb-text-3)' }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0)}`}
                                  tick={{ fontSize: 9, fill: 'var(--rb-text-4)' }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={44}
                                />
                                <Tooltip content={<CustomChartTooltip />} />
                                <Bar dataKey="value" radius={[5, 5, 0, 0]} maxBarSize={56}>
                                  {chartData.map((d, i) => (
                                    <Cell key={i} fill={d.color} fillOpacity={0.8} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Total Summary */}
                  {annualKL > 0 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.99 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className={`rounded-xl border overflow-hidden ${
                        anyCharged
                          ? 'border-red-500/25 bg-red-500/5'
                          : 'border-emerald-500/20 bg-emerald-500/5'
                      }`}
                      style={anyCharged
                        ? { background: 'rgba(239,68,68,0.04)' }
                        : { background: 'rgba(16,185,129,0.04)' }
                      }
                    >
                      <div className={`px-5 py-4 border-b ${
                        anyCharged ? 'border-red-500/15' : 'border-emerald-500/10'
                      }`}>
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              anyCharged ? 'bg-red-500/15' : 'bg-emerald-500/12'
                            }`}>
                              <DollarSign className={`w-5 h-5 ${anyCharged ? 'text-red-400' : 'text-emerald-400'}`} />
                            </div>
                            <div>
                              <p className={`text-[13px] font-black ${anyCharged ? 'text-red-300' : 'text-emerald-300'}`}>
                                Estimated SA Water Excess Charges
                              </p>
                              <p className="text-[9px] text-slate-500 mt-0.5">
                                {activeSamples.length === 0
                                  ? 'no samples selected'
                                  : activeSamples.length === 1
                                    ? 'single sample'
                                    : `${activeSamples.length}-sample avg`
                                } · {fmtNum(annualKL, 0)} kL/yr · excl. GST
                                {selectedSite && ` · ${selectedSite.name}`}
                              </p>
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="flex items-baseline gap-1.5 justify-end">
                              <p className={`text-[26px] font-black tabular-nums tracking-tight ${
                                anyCharged ? 'text-red-400' : 'text-emerald-400'
                              }`}>
                                {fmtAUD(periodTotal)}
                              </p>
                              <span className="text-[11px] text-slate-500 font-bold">/{periodLabel}</span>
                            </div>
                            {anyCharged && (
                              <p className="text-[10px] text-slate-500 mt-0.5">
                                incl. GST: <span className="font-bold text-red-500">{fmtAUD(periodTotal * 1.1)}</span>
                              </p>
                            )}
                            {!anyCharged && (
                              <p className="text-[10px] text-emerald-600 font-bold mt-0.5">
                                ✓ No excess charges triggered
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {anyCharged && (
                        <div className="px-5 py-4">
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                            {chargeRows.filter(r => r.charge !== null && r.charge > 0).map(r => (
                              <div key={r.key} className="text-center px-2 py-2.5 rounded-lg border border-slate-800/40 bg-slate-800/40">
                                <div className="flex items-center justify-center gap-1 mb-1">
                                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: r.color }} />
                                  <p className="text-[9px] font-bold text-slate-500 uppercase">{r.label}</p>
                                </div>
                                <p className="text-[12px] font-black tabular-nums font-mono" style={{ color: r.color }}>
                                  {fmtAUD((r.charge ?? 0) / periodDiv)}
                                </p>
                                <p className="text-[8px] text-slate-600 mt-0.5">/{periodLabel}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action bar — Save + Download */}
                      <div className="px-5 py-3.5 border-t border-slate-800/30 bg-slate-900/30 flex flex-wrap items-center gap-3">
                        <button
                          onClick={handleSaveEstimate}
                          disabled={saving || annualKL <= 0}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-bold bg-teal-600 hover:bg-teal-500 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                        >
                          {saving
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Save className="w-3.5 h-3.5" />
                          }
                          Save Estimate
                        </button>
                        <button
                          onClick={() => handleDownloadPDF()}
                          disabled={annualKL <= 0}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-bold border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download PDF
                        </button>
                        {savedEstimates.length > 0 && (
                          <button
                            onClick={() => setShowEstimates(o => !o)}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold border border-slate-800/50 text-slate-400 hover:bg-slate-700/30 transition-all ml-auto"
                          >
                            <History className="w-3.5 h-3.5" />
                            {savedEstimates.length} Saved
                          </button>
                        )}
                        {saveMsg && (
                          <div className={`flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg ${
                            saveMsg.ok
                              ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20'
                              : 'text-red-400 bg-red-500/10 border border-red-500/20'
                          }`}>
                            {saveMsg.ok
                              ? <CheckCircle className="w-3.5 h-3.5" />
                              : <AlertTriangle className="w-3.5 h-3.5" />
                            }
                            {saveMsg.text}
                          </div>
                        )}
                      </div>

                      {/* Disclaimer */}
                      <div className="px-5 py-3 border-t border-slate-700/20 bg-slate-900/20">
                        <div className="flex items-start gap-2">
                          <TrendingUp className="w-3 h-3 text-slate-600 mt-0.5 shrink-0" />
                          <p className="text-[9px] text-slate-600 leading-relaxed">
                            <strong className="text-slate-500">Estimate only.</strong>{' '}
                            Based on SA Water Fees &amp; Charges Schedule. Mass load = avg conc. (mg/L) × vol. (kL) × 0.001.
                            Confirm with SA Water for formal Trade Waste Agreement. All amounts excl. GST.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* ── Saved Estimates Panel ─────────────────────────────────────────────── */}
      {currentCustomer && selectedSiteId && (
        <AnimatePresence>
            {showEstimates && savedEstimates.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2 }}
                className="rounded-xl border border-teal-500/20 bg-teal-500/4 overflow-hidden"
                style={{ background: 'rgba(20,184,166,0.03)' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-teal-500/15">
                  <div className="flex items-center gap-2.5">
                    <History className="w-4 h-4 text-teal-500" />
                    <p className="text-[12px] font-black text-slate-200">Saved Estimates</p>
                    {loadingEstimates && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />}
                    {!loadingEstimates && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-teal-500/15 text-teal-400 border border-teal-500/20 font-bold">
                        {savedEstimates.length}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-500">
                      {selectedSite?.name}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowEstimates(false)}
                    className="p-1 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-700/40 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* List */}
                <div className="divide-y divide-slate-800/60">
                  {savedEstimates.map((est, i) => {
                    const savedDate = new Date(est.created_at || est.createdAt || Date.now());
                    const dateStr = savedDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                    const timeStr = savedDate.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
                    const total = Number(est.total_charge);
                    const hasCharges = total > 0;
                    return (
                      <div key={est.id || i} className="flex items-center justify-between gap-4 px-5 py-3.5 hover:bg-slate-800/20 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[10px] font-bold text-teal-300">{est.reference_number}</span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${
                              hasCharges
                                ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            }`}>
                              {hasCharges ? fmtAUD(total) + '/yr' : '✓ No charges'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="text-[10px] text-slate-500">{dateStr} {timeStr}</span>
                            {est.sample_label && (
                              <span className="text-[10px] text-slate-600">{est.sample_label}</span>
                            )}
                            <span className="text-[10px] text-slate-600">{fmtNum(Number(est.annual_kl), 0)} kL/yr</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadSavedPDF(est)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border border-slate-800/60 text-slate-400 hover:bg-teal-500/10 hover:border-teal-500/30 hover:text-teal-400 transition-all shrink-0"
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </button>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
        </AnimatePresence>
      )}
    </div>
  );
}
